import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Target, Heart, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import PublicChatWidget from '../components/PublicChatWidget';

const OmOssPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Praktiskt först',
      desc: 'Vi bygger för verkligheten i fält — inte för PowerPoint. Varje funktion ska spara tid på riktigt.',
    },
    {
      icon: Heart,
      title: 'För svenska entreprenörer',
      desc: 'Vi förstår ROT, BAS-kontoplan, fakturerings­krav och vad som faktiskt händer på en arbetsplats i Sverige.',
    },
    {
      icon: ShieldCheck,
      title: 'Trygghet & transparens',
      desc: 'Din data är din. Tydliga priser och fullständig insyn i hur AI:n fattar beslut.',
    },
  ];

  const timeline = [
    {
      year: '2025',
      title: 'Idén föds',
      desc: 'Grundarna såg på nära håll hur mycket tid små och medelstora entreprenadföretag lägger på pappersjobb istället för byggnation.',
    },
    {
      year: '2025',
      title: 'Science Park Skövde',
      desc: 'Operio antas till startup­programmet på Science Park Skövde — en av Sveriges ledande inkubatorer för techbolag.',
    },
    {
      year: '2026',
      title: 'Verifierade av Drivhuset',
      desc: 'Drivhuset i Skaraborg verifierar Operio som ett seriöst tillväxtbolag och stöttar oss i resan från idé till marknad.',
    },
    {
      year: '2026',
      title: 'Lansering',
      desc: 'Operio släpps som publik beta — en komplett plattform för bygg, mark och anläggning med AI inbyggt i kärnan.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }} data-testid="om-oss-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#F7F4EF]/90 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="back-home-link">
            <img src="/operio-logo-2.png" alt="Operio" className="h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#6B6560]">
            <Link to="/#funktioner" className="hover:text-[#1A1A1A] transition-colors">Funktioner</Link>
            <Link to="/#priser" className="hover:text-[#1A1A1A] transition-colors">Priser</Link>
            <Link to="/about" className="text-[#1A1A1A] font-medium">Om oss</Link>
            <Link to="/kontakt" className="hover:text-[#1A1A1A] transition-colors">Kontakt</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-[#6B6560] hover:text-[#1A1A1A] transition-colors">
              Logga in
            </Link>
            <Link to="/register">
              <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white text-sm h-9 px-5 rounded-full">
                Kom igång gratis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <p className="text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-4">Vår historia</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Operio är byggt av personer som tröttnade på pappers­arbete.
            </h1>
            <p className="text-base text-[#6B6560] mt-6 leading-relaxed max-w-xl">
              Vi är ett svenskt startup­bolag som utvecklar nästa generations administrations­plattform för bygg-, mark- och anläggnings­företag. Vårt mål är enkelt: <span className="text-[#1A1A1A] font-medium">låt entreprenörer bygga, så sköter vi resten.</span>
            </p>
          </div>
          <div className="lg:col-span-2 relative flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8613A]/10 via-transparent to-transparent rounded-full blur-3xl" />
            <img src="/operio-mascot-3d.png" alt="Operio maskot" className="relative w-72 h-72 drop-shadow-2xl" data-testid="om-oss-mascot" />
          </div>
        </div>
      </section>

      {/* Backed by */}
      <section className="py-12 px-6 bg-white border-y border-[#E8E2D9]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-medium text-[#7A7570] uppercase tracking-widest text-center mb-8">Stöttade och verifierade av</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#F7F4EF] rounded-xl p-6 border border-[#E8E2D9]" data-testid="science-park-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E8613A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles size={20} className="text-[#E8613A]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1A1A1A]">Science Park Skövde</h3>
                  <p className="text-xs text-[#7A7570] uppercase tracking-wider mt-0.5">Startup­program 2025</p>
                  <p className="text-sm text-[#6B6560] mt-3 leading-relaxed">
                    Operio är antagna till Science Park Skövdes startup­program — en av regionens viktigaste plattformar för tech- och innovations­bolag, med fokus på affärs­utveckling, mentorskap och tillgång till expertis.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-[#F7F4EF] rounded-xl p-6 border border-[#E8E2D9]" data-testid="drivhuset-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E8613A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={20} className="text-[#E8613A]" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1A1A1A]">Drivhuset i Skaraborg</h3>
                  <p className="text-xs text-[#7A7570] uppercase tracking-wider mt-0.5">Verifierat tillväxt­bolag</p>
                  <p className="text-sm text-[#6B6560] mt-3 leading-relaxed">
                    Drivhuset har granskat och verifierat Operio som ett seriöst tillväxt­företag. Vi får löpande stöd inom affärs­utveckling, juridik och finansiering — vilket innebär trygghet för dig som kund.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12">
            <p className="text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-3">Värderingar</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Det här driver oss
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="bg-white rounded-xl p-6 border border-[#E8E2D9]" data-testid={`value-card-${i}`}>
                  <div className="w-10 h-10 bg-[#E8613A]/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#E8613A]" />
                  </div>
                  <h3 className="text-base font-bold text-[#1A1A1A] mb-2">{v.title}</h3>
                  <p className="text-sm text-[#6B6560] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <p className="text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-3">Tidslinje</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Vägen hit
            </h2>
          </div>
          <div className="space-y-8 relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#E8E2D9]" />
            {timeline.map((t, i) => (
              <div key={i} className="flex gap-6 relative" data-testid={`timeline-${i}`}>
                <div className="w-4 h-4 rounded-full bg-[#E8613A] mt-1.5 flex-shrink-0 ring-4 ring-white" />
                <div className="flex-1 pb-2">
                  <p className="text-xs font-bold text-[#7A7570] uppercase tracking-wider">{t.year}</p>
                  <h3 className="text-base font-bold text-[#1A1A1A] mt-1">{t.title}</h3>
                  <p className="text-sm text-[#6B6560] mt-2 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <img src="/operio-mascot-3d.png" alt="Operio maskot" className="w-32 h-32 mx-auto mb-4 drop-shadow-2xl" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vill du följa med på resan?
          </h2>
          <p className="text-white/70 mt-4 max-w-md mx-auto">
            Testa Operio gratis i 14 dagar. Vi är ett ungt bolag — din feedback formar produkten.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <Link to="/register">
              <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30" data-testid="om-oss-cta-btn">
                Kom igång gratis <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-[#E8E2D9]" style={{ backgroundColor: '#F7F4EF' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/operio-logo-2.png" alt="Operio" className="h-6 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-xs text-[#7A7570]">
            <Link to="/#funktioner" className="hover:text-[#1A1A1A]">Funktioner</Link>
            <Link to="/#priser" className="hover:text-[#1A1A1A]">Priser</Link>
            <Link to="/about" className="hover:text-[#1A1A1A]">Om oss</Link>
            <Link to="/kontakt" className="hover:text-[#1A1A1A]">Kontakt</Link>
            <Link to="/login" className="hover:text-[#1A1A1A]">Logga in</Link>
          </nav>
          <p className="text-xs text-[#7A7570]">&copy; 2026 Operio AB</p>
        </div>
      </footer>
      <PublicChatWidget />
    </div>
  );
};

export default OmOssPage;
